import { Box, CircularProgress } from "@mui/material";
import UeCodeChip from "./UeCodeChip";
import DataTable, { ColumnOptions } from "components/DataTable";
import CompaniesTableActions from "./CompaniesTableActions";
import { useCompaniesContext } from "../CompaniesContext";
import CompanyStateChip from "./CompanyStateChip";
import { CompanyWithOrderStats } from "../hooks/useCompaniesWithStats";
import { useAuthContext } from "features/auth/AuthContext";
import { UserRole } from "features/auth/types/user";
import { useMemo } from "react";

type CompaniesTableProps = { companies: CompanyWithOrderStats[] };

const CompaniesTable: React.FC<CompaniesTableProps> = (props) => {
  const { companies } = props;

  const { hasRole } = useAuthContext();
  const canEditCompanies = hasRole([UserRole.ADMIN, UserRole.USER_ADMIN]);

  const { setSelectedCompanyId } = useCompaniesContext();

  const columns: ColumnOptions<CompanyWithOrderStats> = useMemo(
    () => [
      {
        key: "ueCode",
        label: "Lev.kod",
        dense: true,
        renderCell: (company) => (company.ueCode ? <UeCodeChip ueCode={company.ueCode} /> : "-"),
      },
      { key: "name", label: "Företagsnamn", dense: true },
      { key: "city", label: "Ort", dense: true },
      {
        key: "hourlyRate",
        label: "Timpris",
        align: "right",
        renderCell: (company) => company.hourlyRate || "-",
      },
      {
        key: "orders",
        label: "Orders (i år/totalt)",
        align: "right",
        dense: true,
        renderCell: (company: CompanyWithOrderStats) =>
          company.isLoadingStats ? (
            <CircularProgress size="1em" />
          ) : (
            <>
              {company.ordersLastYear}
              <Box component="span" color="text.disabled">
                {" / "}
              </Box>
              {company.totalOrders}
            </>
          ),
      },
      {
        key: "state",
        label: "Status",
        dense: true,
        renderCell: (company: CompanyWithOrderStats) => (
          <CompanyStateChip state={company.state} onboardingState={company.onboardingState} />
        ),
      },
      ...(canEditCompanies
        ? ([
            {
              key: "actions",
              label: "Åtgärder",
              align: "right",
              dense: true,
              renderCell: (company) => <CompaniesTableActions company={company} />,
            },
          ] as ColumnOptions<CompanyWithOrderStats>)
        : []),
    ],
    [canEditCompanies]
  );

  return (
    <DataTable
      data={companies}
      columns={columns}
      getKey={(company) => company.id}
      onClickRow={(company) => setSelectedCompanyId(company.id)}
      emptyMessage="Inga UE-företag matchar sökningen"
      hoverable
      striped
      noBottomBorder
    />
  );
};

export default CompaniesTable;
